import React, { useState, useEffect } from "react";
import { IoChevronUp, IoChevronDown } from "react-icons/io5";
import { format, isAfter, startOfToday } from "date-fns";
import { colors } from "../../../config/colors";
import { DateRangePicker } from 'react-date-range';

// Helper function to ensure end date doesn't exceed today
const getValidEndDate = (endDate) => {
    const today = startOfToday();
    return endDate && isAfter(endDate, today) ? today : endDate;
};

const Filters = ({
    categoryDropdownRef,
    showCategoryDropdown,
    setShowCategoryDropdown,
    selectedCategories,
    setSelectedCategories,
    categorySearchTerm,
    setCategorySearchTerm,
    filteredCategories,
    employeeDropdownRef,
    employeeSearchTerm,
    setEmployeeSearchTerm,
    filteredEmployees,
    showEmployeeDropdown,
    setShowEmployeeDropdown,
    selectedEmployees,
    setSelectedEmployees,
    customerDropdownRef,
    showCustomerDropdown,
    setShowCustomerDropdown,
    selectedCustomers,
    setSelectedCustomers,
    customerSearchTerm,
    setCustomerSearchTerm,
    filteredCustomers,
    taskDropdownRef = null,
    showTaskDropdown = false,
    setShowTaskDropdown = () => {},
    selectedTasks = [],
    setSelectedTasks = () => {},
    taskSearchTerm = "",
    setTaskSearchTerm = () => {},
    filteredTasks = [],
    datePickerRef,
    showDatePicker,
    setShowDatePicker,
    dateRange,
    setDateRange,
    fetchAnalytics,
    fetchCharts,
    colorTheme = 'purple', // 'purple' or 'green',
    showWarning = false,
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [pickerPosition, setPickerPosition] = useState('right');

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            if (datePickerRef.current) {
                const rect = datePickerRef.current.getBoundingClientRect();
                setPickerPosition(rect.left <= window.innerWidth / 2 ? 'left' : 'right');
            }
        };

        const handleClickOutside = (event) => {
            if (!categoryDropdownRef.current?.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
            if (!employeeDropdownRef.current?.contains(event.target)) {
                setShowEmployeeDropdown(false);
            }
            if (!customerDropdownRef.current?.contains(event.target)) {
                setShowCustomerDropdown(false);
            }
            if (!taskDropdownRef?.current?.contains(event.target)) {
                setShowTaskDropdown(false);
            }
            if (!datePickerRef.current?.contains(event.target)) {
                setShowDatePicker(false);
            }
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (showDatePicker && datePickerRef.current) {
            const rect = datePickerRef.current.getBoundingClientRect();
            setPickerPosition(rect.left <= window.innerWidth / 2 ? 'left' : 'right');
        }
    }, [showDatePicker]);

    // Ensure dateRange always has at least one item with the required structure
    const ensuredDateRange = Array.isArray(dateRange) && dateRange.length > 0 
        ? dateRange 
        : [{
            startDate: null,
            endDate: null,
            key: 'selection'
          }];

    const iconColor = colorTheme === 'green' ? '#00B074' : '#7551FF';

    return (
        <div className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-8 w-full" id="filter-row">
                {/* Categories */}
                <div className="flex flex-col flex-1 min-w-[200px] relative" ref={categoryDropdownRef}>
                    <div className="flex items-center justify-between mb-1">
                        <label className="flex items-center gap-2 font-semibold text-[#2B3674]">
                            <span className={`text-[${iconColor}]`}><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                            Categories
                        </label>
                        <span className="bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 text-xs font-medium">{filteredCategories?.length || 0}</span>
                    </div>
                    <button
                        onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                        className={`w-full bg-white rounded-full px-4 py-2 flex items-center justify-between shadow-sm transition-all duration-200 ${
                            selectedCategories.length > 0 
                                ? 'border-gray-500 border-2 ring-2 ring-gray-500' 
                                : 'border border-gray-200'
                        }`}
                    >
                        <span className="flex items-center">
                            {selectedCategories.length === 0 && "All Categories"}
                            {selectedCategories.length === 1 &&
                              filteredCategories.find(cat => cat._id === selectedCategories[0])?.categoryType}
                            {selectedCategories.length === 2 &&
                              filteredCategories
                                .filter(cat => selectedCategories.includes(cat._id))
                                .map(cat => cat.categoryType)
                                .join(", ")}
                            {selectedCategories.length > 2 &&
                              `${filteredCategories.find(cat => selectedCategories.includes(cat._id))?.categoryType}, +${selectedCategories.length - 1} more`}
                        </span>
                        {showCategoryDropdown ? (
                            <IoChevronUp className="ml-2 w-4 h-4 text-gray-400" />
                        ) : (
                            <IoChevronDown className="ml-2 w-4 h-4 text-gray-400" />
                        )}
                    </button>
                    {showCategoryDropdown && (
                        <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                            <div className="p-3">
                                <input
                                    type="text"
                                    placeholder="Search categories..."
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
                                    value={categorySearchTerm}
                                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                                    autoFocus
                                />
                                <div className="mt-2">
                                    <button
                                        onClick={() => {
                                            if (selectedCategories.length === filteredCategories?.length) {
                                                setSelectedCategories([]);
                                            } else {
                                                setSelectedCategories(filteredCategories?.map(cat => cat._id) || []);
                                            }
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-[#166473] hover:bg-gray-50 rounded-md mb-1 font-medium"
                                    >
                                        {selectedCategories.length === filteredCategories?.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="max-h-60 overflow-y-auto">
                                    {filteredCategories?.map((category) => (
                                        <div
                                            key={category._id}
                                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                            onClick={() => {
                                                const isSelected = selectedCategories.includes(category._id);
                                                setSelectedCategories(
                                                    isSelected
                                                        ? selectedCategories.filter((id) => id !== category._id)
                                                        : [...selectedCategories, category._id]
                                                );
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedCategories.includes(category._id)}
                                                readOnly
                                                className="h-4 w-4 text-primary-headings rounded"
                                            />
                                            <span className="ml-3 text-sm">{category.categoryType}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {/* Tasks */}
                {taskDropdownRef && (
                    <div className="flex flex-col flex-1 min-w-[200px] relative" ref={taskDropdownRef}>
                        <div className="flex items-center justify-between mb-1">
                            <label className="flex items-center gap-2 font-semibold text-[#2B3674]">
                                <span className={`text-[${iconColor}]`}><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M9 11H1l8-8 8 8h-8v8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                                Tasks
                            </label>
                            <span className="bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 text-xs font-medium">{filteredTasks?.length || 0}</span>
                        </div>
                        <button
                            onClick={() => setShowTaskDropdown(!showTaskDropdown)}
                            disabled={selectedCategories.length === 0}
                            className={`w-full bg-white rounded-full px-4 py-2 flex items-center justify-between shadow-sm transition-all duration-200 ${
                                selectedCategories.length === 0
                                    ? 'border border-gray-200 bg-gray-50 cursor-not-allowed'
                                    : selectedTasks.length > 0 
                                        ? 'border-gray-100 border-2 ring-2 ring-gray-100' 
                                        : 'border border-gray-200'
                            }`}
                        >
                            <span className="flex items-center">
                                {selectedCategories.length === 0 && "Select categories first"}
                                {selectedCategories.length > 0 && selectedTasks.length === 0 && "All Tasks"}
                                {selectedCategories.length > 0 && selectedTasks.length === 1 &&
                                  filteredTasks.find(task => task._id === selectedTasks[0])?.title}
                                {selectedCategories.length > 0 && selectedTasks.length === 2 &&
                                  filteredTasks
                                    .filter(task => selectedTasks.includes(task._id))
                                    .map(task => task.title)
                                    .join(", ")}
                                {selectedCategories.length > 0 && selectedTasks.length > 2 &&
                                  `${filteredTasks.find(task => selectedTasks.includes(task._id))?.title}, +${selectedTasks.length - 1} more`}
                            </span>
                            {selectedCategories.length > 0 && (
                                showTaskDropdown ? (
                                    <IoChevronUp className="ml-2 w-4 h-4 text-gray-400" />
                                ) : (
                                    <IoChevronDown className="ml-2 w-4 h-4 text-gray-400" />
                                )
                            )}
                        </button>
                        {showTaskDropdown && selectedCategories.length > 0 && (
                            <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                                <div className="p-3">
                                    <input
                                        type="text"
                                        placeholder="Search tasks..."
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
                                        value={taskSearchTerm}
                                        onChange={(e) => setTaskSearchTerm(e.target.value)}
                                        autoFocus
                                    />
                                    <div className="mt-2">
                                        <button
                                            onClick={() => {
                                                if (selectedTasks.length === filteredTasks?.length) {
                                                    setSelectedTasks([]);
                                                } else {
                                                    setSelectedTasks(filteredTasks?.map(task => task._id) || []);
                                                }
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm text-[#166473] hover:bg-gray-50 rounded-md mb-1 font-medium"
                                        >
                                            {selectedTasks.length === filteredTasks?.length ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>
                                    <div className="max-h-60 overflow-y-auto">
                                        {filteredTasks?.map((task) => (
                                            <div
                                                key={task._id}
                                                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                                onClick={() => {
                                                    const isSelected = selectedTasks.includes(task._id);
                                                    setSelectedTasks(
                                                        isSelected
                                                            ? selectedTasks.filter((id) => id !== task._id)
                                                            : [...selectedTasks, task._id]
                                                    );
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTasks.includes(task._id)}
                                                    readOnly
                                                    className="h-4 w-4 text-primary-headings rounded"
                                                />
                                                <span className="ml-3 text-sm">{task.title}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* Employees */}
                <div className="flex flex-col flex-1 min-w-[200px] relative" ref={employeeDropdownRef}>
                    <div className="flex items-center justify-between mb-1">
                        <label className="flex items-center gap-2 font-semibold text-[#2B3674]">
                            <span className={`text-[${iconColor}]`}><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                            Employees
                        </label>
                        <span className="bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 text-xs font-medium">{filteredEmployees?.length || 0}</span>
                    </div>
                    <button
                        onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                        className={`w-full bg-white rounded-full px-4 py-2 flex items-center justify-between shadow-sm transition-all duration-200 ${
                            selectedEmployees.length > 0 
                                ? 'border-gray-100 border-2 ring-2 ring-gray-100' 
                                : 'border border-gray-200'
                        }`}
                    >
                        <span className="flex items-center">
                            {selectedEmployees.length === 0 && "All Employees"}
                            {selectedEmployees.length === 1 &&
                              filteredEmployees.find(emp => emp._id === selectedEmployees[0])?.name}
                            {selectedEmployees.length === 2 &&
                              filteredEmployees
                                .filter(emp => selectedEmployees.includes(emp._id))
                                .map(emp => emp.name)
                                .join(", ")}
                            {selectedEmployees.length > 2 &&
                              `${filteredEmployees.find(emp => selectedEmployees.includes(emp._id))?.name}, +${selectedEmployees.length - 1} more`}
                        </span>
                        {showEmployeeDropdown ? (
                            <IoChevronUp className="ml-2 w-4 h-4 text-gray-400" />
                        ) : (
                            <IoChevronDown className="ml-2 w-4 h-4 text-gray-400" />
                        )}
                    </button>
                    {showEmployeeDropdown && (
                        <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                            <div className="p-3">
                                <input
                                    type="text"
                                    placeholder="Search employees..."
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
                                    value={employeeSearchTerm}
                                    onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                                    autoFocus
                                />
                                <div className="mt-2">
                                    <button
                                        onClick={() => {
                                            if (selectedEmployees.length === filteredEmployees?.length) {
                                                setSelectedEmployees([]);
                                            } else {
                                                setSelectedEmployees(filteredEmployees?.map(emp => emp._id) || []);
                                            }
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-[#166473] hover:bg-gray-50 rounded-md mb-1 font-medium"
                                    >
                                        {selectedEmployees.length === filteredEmployees?.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="max-h-60 overflow-y-auto">
                                    {filteredEmployees?.map((employee) => (
                                        <div
                                            key={employee._id}
                                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                            onClick={() => {
                                                const isSelected = selectedEmployees.includes(employee._id);
                                                setSelectedEmployees(
                                                    isSelected
                                                        ? selectedEmployees.filter((id) => id !== employee._id)
                                                        : [...selectedEmployees, employee._id]
                                                );
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedEmployees.includes(employee._id)}
                                                readOnly
                                                className="h-4 w-4 text-primary-headings rounded"
                                            />
                                            <span className="ml-3 text-sm">{employee.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {/* Customers */}
                <div className="flex flex-col flex-1 min-w-[200px] relative" ref={customerDropdownRef}>
                    <div className="flex items-center justify-between mb-1">
                        <label className="flex items-center gap-2 font-semibold text-[#2B3674]">
                            <span className={`text-[${iconColor}]`}><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
                            Customers
                        </label>
                        <span className="bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 text-xs font-medium">{filteredCustomers?.length || 0}</span>
                    </div>
                    <button
                        onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                        className={`w-full bg-white rounded-full px-4 py-2 flex items-center justify-between shadow-sm transition-all duration-200 ${
                            selectedCustomers.length > 0 
                                ? 'border-gray-100 border-2 ring-2 ring-gray-100' 
                                : 'border border-gray-200'
                        }`}
                    >
                        <span className="flex items-center">
                            {selectedCustomers.length === 0 && "All Customers"}
                            {selectedCustomers.length === 1 &&
                              filteredCustomers.find(cust => cust._id === selectedCustomers[0])?.name}
                            {selectedCustomers.length === 2 &&
                              filteredCustomers
                                .filter(cust => selectedCustomers.includes(cust._id))
                                .map(cust => cust.name)
                                .join(", ")}
                            {selectedCustomers.length > 2 &&
                              `${filteredCustomers.find(cust => selectedCustomers.includes(cust._id))?.name}, +${selectedCustomers.length - 1} more`}
                        </span>
                        {showCustomerDropdown ? (
                            <IoChevronUp className="ml-2 w-4 h-4 text-gray-400" />
                        ) : (
                            <IoChevronDown className="ml-2 w-4 h-4 text-gray-400" />
                        )}
                    </button>
                    {showCustomerDropdown && (
                        <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                            <div className="p-3">
                                <input
                                    type="text"
                                    placeholder="Search customers..."
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
                                    value={customerSearchTerm}
                                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                    autoFocus
                                />
                                <div className="mt-2">
                                    <button
                                        onClick={() => {
                                            if (selectedCustomers.length === filteredCustomers?.length) {
                                                setSelectedCustomers([]);
                                            } else {
                                                setSelectedCustomers(filteredCustomers?.map(cust => cust._id) || []);
                                            }
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-[#166473] hover:bg-gray-50 rounded-md mb-1 font-medium"
                                    >
                                        {selectedCustomers.length === filteredCustomers?.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div className="max-h-60 overflow-y-auto">
                                    {filteredCustomers?.map((customer) => (
                                        <div
                                            key={customer._id}
                                            className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                            onClick={() => {
                                                const isSelected = selectedCustomers.includes(customer._id);
                                                setSelectedCustomers(
                                                    isSelected
                                                        ? selectedCustomers.filter((id) => id !== customer._id)
                                                        : [...selectedCustomers, customer._id]
                                                );
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedCustomers.includes(customer._id)}
                                                readOnly
                                                className="h-4 w-4 text-primary-headings rounded"
                                            />
                                            <span className="ml-3 text-sm">{customer.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {/* Date Range */}
                <div className="flex flex-col flex-1 min-w-[200px] relative" ref={datePickerRef}>
                    <div className="flex items-center justify-between mb-1">
                        <label className="flex items-center gap-2 font-semibold text-[#2B3674]">
                            <span className={`text-[${iconColor}]`}><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></span>
                            Date Range
                        </label>
                        <span className="invisible select-none">0</span>
                    </div>
                    <button
                        onClick={() => setShowDatePicker(!showDatePicker)}
                        className={`w-full bg-white rounded-full px-4 py-2 flex items-center justify-between shadow-sm transition-all duration-200 ${
                            ensuredDateRange[0]?.startDate && ensuredDateRange[0]?.endDate
                                ? 'border-gray-100 border-2 ring-2 ring-gray-100' 
                                : 'border border-gray-200'
                        }`}
                    >
                        <span className="flex items-center">
                            {ensuredDateRange[0] && ensuredDateRange[0].startDate && ensuredDateRange[0].endDate
                                ? `${format(ensuredDateRange[0].startDate, "MMM dd, yyyy")} - ${format(ensuredDateRange[0].endDate, "MMM dd, yyyy")}`
                                : "Date Range"}
                        </span>
                        {showDatePicker ? (
                            <IoChevronUp className="ml-2 w-4 h-4 text-gray-400" />
                        ) : (
                            <IoChevronDown className="ml-2 w-4 h-4 text-gray-400" />
                        )}
                    </button>
                    {showDatePicker && (
                        <div 
                            className={`absolute top-full mt-2 z-50 bg-white rounded-lg shadow-lg border border-gray-200 ${pickerPosition === 'right' ? 'right-0' : 'left-0'}`}
                        >
                            <DateRangePicker
                                onChange={(item) => {
                                    // Get the first selection key from the item object
                                    const selectionKey = Object.keys(item)[0];
                                    // Ensure we're setting a valid date range object with end date not exceeding today
                                    setDateRange([{
                                        startDate: item[selectionKey].startDate,
                                        endDate: getValidEndDate(item[selectionKey].endDate),
                                        key: ensuredDateRange[0].key || 'selection'
                                    }]);
                                }}
                                showSelectionPreview={true}
                                moveRangeOnFirstSelection={false}
                                months={isMobile ? 1 : 2}
                                ranges={ensuredDateRange}
                                direction={isMobile ? "vertical" : "horizontal"}
                                rangeColors={[colors.primary.headingsBody]}
                                maxDate={startOfToday()}
                            />
                            <div className="border-t mt-2 pt-2 flex justify-between px-4 pb-2">
                                <button
                                    onClick={() => {
                                        setDateRange([
                                            {
                                                startDate: null,
                                                endDate: null,
                                                key: "selection",
                                            },
                                        ]);
                                        setShowDatePicker(false);
                                        fetchAnalytics();
                                        if (fetchCharts) {
                                            fetchCharts();
                                        }
                                    }}
                                    className="text-sm text-red-500 hover:text-red-700"
                                >
                                    Clear
                                </button>
                                <button
                                    onClick={() => {
                                        setShowDatePicker(false);
                                        fetchAnalytics();
                                        if (fetchCharts) {
                                            fetchCharts();
                                        }
                                    }}
                                    className="text-sm text-primary-headings hover:text-primary-light"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* Warning/Info Box below Date Range, aligned under the last filter using grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 w-full">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
  
              {showWarning && (
                <div className="ml-6">
                    <div className="mt-3 bg-[#FFF4E5] border border-[#FFE1B0] rounded-xl px-4 py-2 flex items-center gap-2 max-w-[350px]">
                    <svg width="20" height="20" className="w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <path d="M12 9v4M12 17h.01" stroke="#B26A00" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="12" cy="12" r="10" stroke="#B26A00" strokeWidth="2"/>
                    </svg>
                    <span className="text-[#B26A00] font-normal text-xs">
                        This date range is not applicable for comparison charts.
                    </span>
                  </div>
                </div>
              )}
            </div>
        </div>
    );
};

export default Filters;
